import type { AST } from '@eslint-community/regexpp';
import { parseRegExpLiteral } from '@eslint-community/regexpp';

export type RegexInputFormat = 'auto' | 'pattern' | 'literal';

export interface ParseRegexOptions {
  inputFormat?: RegexInputFormat;
  flags?: string;
}

export type ParseRegexResult =
  | {
      ok: true;
      ast: AST.RegExpLiteral;
      pattern: string;
      flags: string;
    }
  | {
      ok: false;
      error: string;
    };

function stripCommonPrefixes(text: string): string {
  // Regulex commonly shows regexes as: "RegExp: /.../flags"
  // Accept a few common prefixes without being overly permissive.
  return text.replace(/^\s*(?:regexp|regex)\s*:\s*/i, '').trim();
}

function findClosingSlash(literal: string): number {
  // Scan from index 1 and find the first unescaped '/' that is not inside a character class.
  let escaped = false;
  let inCharClass = false;

  for (let i = 1; i < literal.length; i++) {
    const ch = literal[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (ch === '\\') {
      escaped = true;
      continue;
    }

    if (ch === '[') {
      inCharClass = true;
      continue;
    }

    if (ch === ']' && inCharClass) {
      inCharClass = false;
      continue;
    }

    if (ch === '/' && !inCharClass) {
      return i;
    }
  }

  return -1;
}

function tryParseLiteral(text: string): { pattern: string; flags: string } | null {
  const t = stripCommonPrefixes(text);
  if (!t.startsWith('/')) return null;

  const closeIndex = findClosingSlash(t);
  if (closeIndex <= 1) return null;

  const pattern = t.slice(1, closeIndex);
  const flags = t.slice(closeIndex + 1);

  // Keep parsing predictable when pasting prose like "/foo/ bar".
  if (/\s/.test(flags)) return null;

  return { pattern, flags };
}

export function parseRegex(input: string, options: ParseRegexOptions = {}): ParseRegexResult {
  const text = stripCommonPrefixes(input);
  if (!text) return { ok: false, error: 'Input is empty' };

  const inputFormat: RegexInputFormat =
    options.inputFormat === 'pattern' || options.inputFormat === 'literal' || options.inputFormat === 'auto'
      ? options.inputFormat
      : 'auto';

  let pattern = text;
  let flags = String(options.flags ?? '').trim();

  if (inputFormat === 'literal' || inputFormat === 'auto') {
    const lit = tryParseLiteral(text);
    if (lit) {
      pattern = lit.pattern;
      flags = lit.flags;
    } else if (inputFormat === 'literal') {
      return { ok: false, error: 'Expected a JavaScript regex literal like /pattern/flags' };
    }
  }

  // Ensure flags don't contain whitespace
  if (/\s/.test(flags)) {
    return { ok: false, error: `Invalid flags: "${flags}"` };
  }

  try {
    const re = new RegExp(pattern, flags);
    const ast = parseRegExpLiteral(re);
    return { ok: true, ast, pattern, flags };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unable to parse regex';
    return { ok: false, error: message };
  }
}
